import React from "react";
import { ColumnDef } from "@tanstack/react-table";
import CopyToClipboard from "react-copy-to-clipboard";
import toast from "react-hot-toast";
import { Copy, ExternalLink, MoreHorizontal, Pencil, QrCode, Receipt } from "lucide-react";

import DataTableColumnHeader from "../../../components/data-table/DataTableColumnHeader";
import StatusBadge from "../../../components/StatusBadge";
import { Button } from "../../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import { APP_SINGLE_BUSINESS_URL } from "../../../constant";
import { formatLongDate } from "../../../utils/formatters";
import { formatAmount } from "../../../utils/wordFunctions";

export type PaymentPageBank = {
  accountNumber?: string;
  bankCode?: string;
  accountName?: string;
};

export type PaymentPageRow = {
  reference: string;
  name?: string;
  description?: string;
  /** Stored in naira. */
  amount?: number;
  createdAt?: string;
  status?: string;
  /** The payout bank tied to the page; absent on pages created before banks. */
  bank?: PaymentPageBank;
};

type Options = {
  /** USDT → NGN rate, used to show the dollar equivalent. */
  rate: number;
  onViewTransactions: (row: PaymentPageRow) => void;
  onShowQr: (row: PaymentPageRow) => void;
  onEdit: (row: PaymentPageRow) => void;
};

const pageUrl = (reference?: string) =>
  `${APP_SINGLE_BUSINESS_URL}/${reference}`;

export const buildPaymentColumns = ({
  rate,
  onViewTransactions,
  onShowQr,
  onEdit,
}: Options): ColumnDef<PaymentPageRow, any>[] => [
  {
    accessorKey: "reference",
    meta: { label: "Reference" },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Reference" />
    ),
    cell: ({ row }) => (
      <span className="font-mono text-[13px] font-semibold text-foreground">
        #{row.original.reference}
      </span>
    ),
  },
  {
    accessorKey: "name",
    meta: { label: "Page" },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Page" />,
    cell: ({ row }) => (
      <div className="max-w-[240px]">
        <p className="truncate text-[14px] font-semibold text-foreground">
          {row.original.name || "Untitled page"}
        </p>
        {row.original.description && (
          <p className="truncate text-[12px] font-medium text-muted-foreground">
            {row.original.description}
          </p>
        )}
      </div>
    ),
  },
  {
    accessorKey: "amount",
    meta: { label: "Amount" },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Amount" />
    ),
    cell: ({ row }) => {
      const amount = row.original.amount;
      if (!amount) {
        return <span className="text-[14px] text-muted-foreground">Customer sets</span>;
      }
      return (
        <div>
          <p className="text-[14px] font-semibold text-foreground">
            ${rate ? formatAmount(amount / rate) : "—"}
          </p>
          <p className="text-[12px] font-medium text-muted-foreground">
            {formatAmount(amount)} NGN
          </p>
        </div>
      );
    },
  },
  {
    id: "createdAt",
    accessorKey: "createdAt",
    meta: { label: "Created" },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Created" />
    ),
    cell: ({ row }) => (
      <span className="text-[13px] font-medium text-muted-foreground">
        {formatLongDate(row.original.createdAt)}
      </span>
    ),
  },
  {
    id: "status",
    meta: { label: "Status" },
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    // The API does not yet return a status for payment pages; every created
    // page is live. Read it if it ever appears, otherwise fall back.
    cell: ({ row }) => <StatusBadge status={row.original.status ?? "LIVE"} />,
  },
  {
    id: "actions",
    enableHiding: false,
    enableSorting: false,
    header: () => <span className="sr-only">Actions</span>,
    cell: ({ row }) => (
      <div
        className="flex justify-end"
        onClick={(event) => event.stopPropagation()}
      >
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="size-8">
              <MoreHorizontal className="size-4" />
              <span className="sr-only">Open actions</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52 font-manrope">
            <DropdownMenuLabel>Payment page</DropdownMenuLabel>
            <DropdownMenuSeparator />

            <DropdownMenuItem onClick={() => onEdit(row.original)}>
              <Pencil className="mr-2 size-4" />
              Edit page
            </DropdownMenuItem>

            <DropdownMenuItem onClick={() => onViewTransactions(row.original)}>
              <Receipt className="mr-2 size-4" />
              View transactions
            </DropdownMenuItem>

            <CopyToClipboard
              text={pageUrl(row.original.reference)}
              onCopy={() => toast.success("Payment page URL copied")}
            >
              <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                <Copy className="mr-2 size-4" />
                Copy payment link
              </DropdownMenuItem>
            </CopyToClipboard>

            <DropdownMenuItem onClick={() => onShowQr(row.original)}>
              <QrCode className="mr-2 size-4" />
              Show QR code
            </DropdownMenuItem>

            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() =>
                window.open(pageUrl(row.original.reference), "_blank")
              }
            >
              <ExternalLink className="mr-2 size-4" />
              Visit page
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    ),
  },
];
